import { useEffect, useMemo, useState } from 'react';

import InstagramMockPreview from './InstagramMockPreview';
import { useI18n } from '../i18n/I18nProvider';
import StudioIcon from './StudioIcon';
import GenerationStatus from './GenerationStatus';

export default function PreviewHero({
  previewUrl,
  textOutput,
  sizeLabel,
  modelLabel,
  seedLabel,
  onLockSeed,
  onCopyUrl,
  onCopyText,
  onOpen,
  onDownload,
  onDownloadAll,
  onRetry,
  canLockSeed,
  canDownload,
  canDownloadAll,
  isLoading,
  errorMessage,
  errorActionLabel,
  onImageLoad,
  onImageError,
  mediaType,
  logoOverlay,
  onUpdateLogoOverlay,
  brandName,
  avatarDataUrl,
  caption,
}) {
  const { t } = useI18n();
  const mt = String(mediaType || '').trim() || 'image';
  const canInstagram = mt === 'image' || mt === 'video';

  const [igPreview, setIgPreview] = useState(() => {
    try {
      return String(localStorage.getItem('brand_studio_ig_preview_v1') || '') === '1';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem('brand_studio_ig_preview_v1', igPreview ? '1' : '0');
    } catch {
      // ignore
    }
  }, [igPreview]);

  const [igVariant, setIgVariant] = useState(() => {
    try {
      const v = String(localStorage.getItem('brand_studio_ig_variant_v1') || '').trim();
      if (v === 'feed' || v === 'story' || v === 'profile') return v;
      return 'profile';
    } catch {
      return 'profile';
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem('brand_studio_ig_variant_v1', igVariant);
    } catch {
      // ignore
    }
  }, [igVariant]);

  const resolvedIgVariant = useMemo(() => {
    if (!canInstagram) return 'feed';
    if (igVariant === 'feed' || igVariant === 'story' || igVariant === 'profile') return igVariant;
    return 'profile';
  }, [canInstagram, igVariant]);

  const igCaption = useMemo(() => {
    const c = String(caption || '').trim();
    if (c) return c;
    return String(textOutput || '').trim();
  }, [caption, textOutput]);
  return (
    <div className="PreviewCard" id="studio-canvas" tabIndex={-1}>
      <div className="CanvasLabel"><span><StudioIcon name={mt} size={17} /> {t('studio.canvas')}</span><small className={isLoading ? 'CanvasStatus--busy' : errorMessage ? 'CanvasStatus--error' : ''}><i /> {isLoading ? t('common.generating') : errorMessage ? t('common.error') : previewUrl || textOutput ? t('common.preview') : t('studio.ready')}</small></div>
      <div className="PreviewHeader">
        <div className="Chips">
          {sizeLabel ? <span className="Chip">{sizeLabel}</span> : null}
          {modelLabel ? <span className="Chip">{modelLabel}</span> : null}
          {seedLabel ? <span className="Chip">{seedLabel}</span> : null}
        </div>
        <div className="PreviewActions">
          {mediaType === 'text' ? (
            <>
              <button className="Btn Btn--ghost" type="button" onClick={onCopyText} disabled={isLoading || !textOutput}>
                {t('common.copyOutput')}
              </button>
              <button className="Btn" type="button" onClick={onDownload} disabled={!canDownload}>
                {t('common.download')}
              </button>
            </>
          ) : (
            <>
              {canInstagram ? (
                <button className="Btn Btn--ghost" type="button" disabled={isLoading} onClick={() => setIgPreview((v) => !v)}>
                  {igPreview ? t('preview.instagramOff') : t('preview.instagramOn')}
                </button>
              ) : null}
              {canInstagram && igPreview ? (
                <div className="Seg" role="tablist" aria-label={t('common.preview')}>
                  <button
                    type="button"
                    className={`SegBtn ${resolvedIgVariant === 'profile' ? 'SegBtn--active' : ''}`}
                    onClick={() => setIgVariant('profile')}
                  >
                    {t('preview.instagramProfile')}
                  </button>
                  <button
                    type="button"
                    className={`SegBtn ${resolvedIgVariant === 'feed' ? 'SegBtn--active' : ''}`}
                    onClick={() => setIgVariant('feed')}
                  >
                    {t('preview.instagramPost')}
                  </button>
                  <button
                    type="button"
                    className={`SegBtn ${resolvedIgVariant === 'story' ? 'SegBtn--active' : ''}`}
                    onClick={() => setIgVariant('story')}
                  >
                    {t('preview.instagramStory')}
                  </button>
                </div>
              ) : null}
              <button className="Btn" type="button" onClick={onDownload} disabled={!canDownload}>
                {t('common.download')}
              </button>
              <details className="PreviewMore">
                <summary className="IconBtn" aria-label="More actions">•••</summary>
                <div className="PreviewMoreMenu">
                  <button className="Btn Btn--ghost" type="button" onClick={onOpen} disabled={!previewUrl || isLoading}>{t('common.open')}</button>
                  <button className="Btn Btn--ghost" type="button" onClick={onRetry} disabled={!previewUrl || isLoading}>{t('common.retry')}</button>
                  <button className="Btn Btn--ghost" type="button" onClick={onCopyUrl} disabled={!previewUrl || isLoading}>{t('common.copyUrl')}</button>
                  <button className="Btn Btn--ghost" type="button" onClick={onLockSeed} disabled={!canLockSeed}>{t('preview.lockSeed')}</button>
                  {canDownloadAll ? <button className="Btn Btn--ghost" type="button" onClick={onDownloadAll}>{t('common.downloadAll')}</button> : null}
                </div>
              </details>
            </>
          )}
        </div>
      </div>

      <div className="PreviewStage">
        <div className="PreviewContents" aria-busy={Boolean(isLoading)} inert={Boolean(isLoading || errorMessage)}>
        {mediaType === 'text' && (textOutput || isLoading || errorMessage) ? (
          <div className="PreviewFrame">
            <pre className="TextResult">{textOutput || '—'}</pre>
          </div>
        ) : previewUrl ? (
          <div className="PreviewFrame PreviewFrame--media">
            {mediaType === 'video' ? (
              igPreview ? (
                <InstagramMockPreview
                  previewUrl={previewUrl}
                  mediaType="video"
                  brandName={brandName}
                  avatarDataUrl={avatarDataUrl}
                  caption={igCaption}
                  variant={resolvedIgVariant}
                  onMediaLoad={onImageLoad}
                  onMediaError={onImageError}
                />
              ) : (
                <video
                  className="PreviewImage"
                  src={previewUrl}
                  controls
                  playsInline
                  onLoadedData={onImageLoad}
                  onError={onImageError}
                />
              )
            ) : mediaType === 'audio' ? (
              <audio className="PreviewAudio" src={previewUrl} controls onCanPlayThrough={onImageLoad} onError={onImageError} />
            ) : (
              igPreview ? (
                <InstagramMockPreview
                  previewUrl={previewUrl}
                  mediaType="image"
                  brandName={brandName}
                  avatarDataUrl={avatarDataUrl}
                  caption={igCaption}
                  variant={resolvedIgVariant}
                  onMediaLoad={onImageLoad}
                  onMediaError={onImageError}
                />
              ) : (
                <div style={{ position: 'relative' }}>
                  <img
                    className="PreviewImage"
                    src={previewUrl}
                    alt={t('preview.generatedAlt')}
                    onLoad={onImageLoad}
                    onError={onImageError}
                  />
                  {logoOverlay?.enabled && logoOverlay?.logoDataUrl ? (
                    <img
                      src={logoOverlay.logoDataUrl}
                      alt=""
                      aria-hidden="true"
                      style={{
                        position: 'absolute',
                        pointerEvents: 'none',
                        opacity: logoOverlay.opacity ?? 0.9,
                        width: `${logoOverlay.sizePct ?? 18}%`,
                        height: 'auto',
                        ...(logoOverlay.position === 'top_left'
                          ? { top: logoOverlay.padding ?? 18, left: logoOverlay.padding ?? 18 }
                          : logoOverlay.position === 'top_right'
                            ? { top: logoOverlay.padding ?? 18, right: logoOverlay.padding ?? 18 }
                            : logoOverlay.position === 'bottom_left'
                              ? { bottom: logoOverlay.padding ?? 18, left: logoOverlay.padding ?? 18 }
                              : logoOverlay.position === 'center'
                                ? { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }
                                : { bottom: logoOverlay.padding ?? 18, right: logoOverlay.padding ?? 18 }),
                      }}
                    />
                  ) : null}
                </div>
              )
            )}

            {!igPreview && mediaType === 'image' && logoOverlay?.logoDataUrl ? (
              <div className="CardFlat" style={{ marginTop: 12 }}>
                <div className="Row Row--tight" style={{ justifyContent: 'space-between' }}>
                  <div className="Hint">{t('preview.logoOverlayTitle')}</div>
                  {logoOverlay.enabled ? (
                    <button className="Btn Btn--ghost" type="button" onClick={() => onUpdateLogoOverlay?.({ logoOverlayEnabled: false })}>
                      {t('preview.logoOverlayRemove')}
                    </button>
                  ) : (
                    <button className="Btn Btn--ghost" type="button" onClick={() => onUpdateLogoOverlay?.({ logoOverlayEnabled: true })}>
                      {t('preview.logoOverlayAdd')}
                    </button>
                  )}
                </div>

                {logoOverlay.enabled ? (
                  <div className="Grid Grid--single" style={{ marginTop: 10 }}>
                    <label className="Field">
                      <div className="Field-label">{t('brand.logoOverlayPosition')}</div>
                      <select
                        value={logoOverlay.position || 'bottom_right'}
                        onChange={(e) => onUpdateLogoOverlay?.({ logoOverlayPosition: e.target.value })}
                      >
                        <option value="top_left">{t('brand.logoOverlayPositions.top_left')}</option>
                        <option value="top_right">{t('brand.logoOverlayPositions.top_right')}</option>
                        <option value="bottom_left">{t('brand.logoOverlayPositions.bottom_left')}</option>
                        <option value="bottom_right">{t('brand.logoOverlayPositions.bottom_right')}</option>
                        <option value="center">{t('brand.logoOverlayPositions.center')}</option>
                      </select>
                    </label>

                    <label className="Field">
                      <div className="Field-label">{t('brand.logoOverlaySize')}</div>
                      <input
                        type="number"
                        min={5}
                        max={40}
                        step={1}
                        value={logoOverlay.sizePct ?? 18}
                        onChange={(e) => onUpdateLogoOverlay?.({ logoOverlaySizePct: e.target.value })}
                      />
                    </label>

                    <label className="Field">
                      <div className="Field-label">{t('brand.logoOverlayOpacity')}</div>
                      <input
                        type="number"
                        min={5}
                        max={100}
                        step={1}
                        value={Math.round((Number(logoOverlay.opacity ?? 0.9) || 0.9) * 100)}
                        onChange={(e) => onUpdateLogoOverlay?.({ logoOverlayOpacity: String((Number(e.target.value) || 0) / 100) })}
                      />
                    </label>

                    <label className="Field">
                      <div className="Field-label">{t('brand.logoOverlayPadding')}</div>
                      <input
                        type="number"
                        min={0}
                        max={80}
                        step={1}
                        value={logoOverlay.padding ?? 18}
                        onChange={(e) => onUpdateLogoOverlay?.({ logoOverlayPadding: e.target.value })}
                      />
                    </label>
                  </div>
                ) : null}
              </div>
            ) : null}

          </div>
        ) : !isLoading && !errorMessage ? (
          <div className="PreviewEmpty">
            <div className="CanvasArtwork" aria-hidden="true">
              <div className="ArtworkOrbit ArtworkOrbit--one" />
              <div className="ArtworkOrbit ArtworkOrbit--two" />
              <div className="ArtworkOrb" />
              <div className="ArtworkCard ArtworkCard--back"><span>CF</span><div /><div /></div>
              <div className="ArtworkCard ArtworkCard--front"><StudioIcon name={mt} size={28} /><div /><span>MAKE IT YOURS.</span></div>
              <span className="ArtworkSpark ArtworkSpark--one">✦</span><span className="ArtworkSpark ArtworkSpark--two">+</span>
            </div>
            <div className="PreviewEmptyTitle">{t('studio.emptyTitle')}</div>
            <div className="PreviewEmptyHint">{t('studio.emptyHint')}</div>
            <div className="CanvasBenefits"><span>✦ {t('studio.brandAware')}</span><span>↗ {t('studio.exportReady')}</span></div>
          </div>
        ) : null}
        </div>
        {isLoading ? <GenerationStatus mediaType={mt} /> : null}
        {!isLoading && errorMessage ? (
          <div className="CanvasError" role="alert">
            <div className="CanvasErrorMark" aria-hidden="true">!</div>
            <h2>{t('generation.failed')}</h2>
            <p>{errorMessage}</p>
            <button className="Btn" type="button" onClick={onRetry}>{errorActionLabel || t('common.retry')}</button>
          </div>
        ) : null}
      </div>
    </div>
  );
}
